import React from 'react';
import { NavLink, Outlet, useNavigate, Navigate } from 'react-router-dom';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import {
  LayoutDashboard, ArrowDownToLine, ArrowUpFromLine, Users, CreditCard, Landmark, BarChart3, LogOut, Zap,
} from 'lucide-react';
import { getBranchToken, getBranchUser, branchLogout } from '../../services/branchApi';

const ROLE_LABEL: Record<string, string> = { super_master: 'Super Master', agent: 'Agent', executive: 'Executive' };

// Build the nav for the logged-in role. Super Master configures + reports (no
// approvals); Agents/Executives only see the action tabs they were granted.
function navFor(user: any) {
  const items: { to: string; label: string; icon: any }[] = [];
  const isSM = user?.isManager;
  items.push({ to: '/branch/dashboard', label: 'Dashboard', icon: LayoutDashboard });
  if (isSM || user?.canDeposit) items.push({ to: '/branch/deposits', label: 'Manual Deposit', icon: ArrowDownToLine });
  if (isSM || user?.canWithdraw) items.push({ to: '/branch/withdrawals', label: 'Manual Withdraw', icon: ArrowUpFromLine });
  // Gateway (crypto) payouts — same withdraw capability, separate queue because
  // approving sends the money instead of recording a hand-made bank transfer.
  if (isSM || user?.canWithdraw) items.push({ to: '/branch/auto-withdrawals', label: 'Auto Withdraw', icon: Zap });
  // Super Master manages agents + executives; agents manage their executives.
  if (isSM || user?.canCreateExecutives) items.push({ to: '/branch/staff', label: isSM ? 'Agents & Executives' : 'My Executives', icon: Users });
  if (isSM) items.push({ to: '/branch/accounts', label: 'Deposit Accounts', icon: Landmark });
  if (isSM) items.push({ to: '/branch/bank-pool', label: 'Bank Pool', icon: CreditCard });
  items.push({ to: '/branch/reports', label: 'Reports', icon: BarChart3 });
  return items;
}

export default function BranchLayout() {
  const nav = useNavigate();
  if (!getBranchToken()) return <Navigate to="/branch/login" replace />;
  const user = getBranchUser();
  const items = navFor(user);
  const roleLabel = ROLE_LABEL[user?.kind] || (user?.isManager ? 'Super Master' : 'Agent');

  const logout = () => { branchLogout(); nav('/branch/login', { replace: true }); };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* sidebar */}
      <Box sx={{ width: 240, bgcolor: 'background.paper', borderRight: 1, borderColor: 'divider', p: 2, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>BRANCH PORTAL</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
          {user?.branch?.name || 'Branch'}
        </Typography>
        <Chip size="small" label={roleLabel} color={user?.isManager ? 'primary' : 'default'} sx={{ mb: 2, alignSelf: 'flex-start' }} />
        <Stack spacing={0.5} sx={{ flex: 1 }}>
          {items.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} style={{ textDecoration: 'none' }}>
              {({ isActive }) => (
                <Stack direction="row" spacing={1.5} alignItems="center"
                  sx={{
                    px: 1.5, py: 1, borderRadius: 1,
                    color: isActive ? 'primary.main' : 'text.secondary',
                    bgcolor: isActive ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}>
                  <Icon size={18} />
                  <Typography variant="body2" fontWeight={isActive ? 700 : 500}>{label}</Typography>
                </Stack>
              )}
            </NavLink>
          ))}
        </Stack>
        <Button startIcon={<LogOut size={16} />} color="error" onClick={logout} sx={{ justifyContent: 'flex-start' }}>
          Logout
        </Button>
      </Box>
      {/* content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Box sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={700}>{user?.branch?.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.name} · {roleLabel}
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}><Outlet /></Box>
      </Box>
    </Box>
  );
}
